<template>
  <div class="invoices" v-if="getInvoices">
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getInvoices"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getInvoiceCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h4 class="page-title">{{ $t('invoices') }}</h4>
          </div>
          <div class="table-header-sums">
            <span
              >{{ $t('sum') }} {{ $t('grossAmount') }}: {{ formatCurrency(sumGrossAmount) }}</span
            >
            <span>{{ $t('sum') }} {{ $t('netAmount') }}: {{ formatCurrency(sumNetAmount) }}</span>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>

            <button class="btn btn-sm btn-inverse" @click="$emit('create-invoice')">
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('invoice_create') }}
            </button>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <!-- Invoice Number (internal) -->
      <Column
        field="number"
        header="#"
        sortable
        :headerStyle="{ width: '60px' }"
        :bodyStyle="'text-align: center; width: 60px'"
        filterField="number"
      >
        <template #body="slotProps">
          <router-link
            v-if="slotProps.data && slotProps.data.number !== null"
            :to="{
              name: 'InvoiceEditPage',
              params: { invoiceNumber: slotProps.data.number },
            }"
          >
            {{ slotProps.data.number.toString().padStart(4, '0') }}
          </router-link>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Invoice Number (external) -->
      <Column
        field="invoiceNumber"
        :header="$t('invoiceNumber')"
        sortable
        filterMatchMode="contains"
        filterField="invoiceNumber"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.invoiceNumber">
            {{ slotProps.data.invoiceNumber }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Invoice Type -->
      <Column
        field="invoiceType"
        :header="$t('invoiceType')"
        sortable
        :headerStyle="{ width: '140px' }"
        :bodyStyle="'text-align: center; width: 140px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data && slotProps.data.invoiceType"
            :variant="getInvoiceTypeColor(slotProps.data.invoiceType)"
          >
            {{ $t(slotProps.data.invoiceType) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.invoiceTypes"
            :options="invoiceTypes"
            @input="onInvoiceTypeFilter"
            optionLabel="label"
            :placeholder="$t('invoiceType')"
            display="chip"
            :style="{ 'max-width': '140px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getInvoiceTypeColor(slotProps.option.value)">
                {{ $t(slotProps.option.value) }}
              </b-badge>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Invoice State -->
      <Column
        field="invoiceState"
        header="Status"
        sortable
        :headerStyle="{ width: '140px' }"
        :bodyStyle="'text-align: center; width: 140px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge
            v-if="slotProps.data && slotProps.data.invoiceState"
            :variant="getInvoiceStateColor(slotProps.data.invoiceState)"
          >
            {{ $t(slotProps.data.invoiceState) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.invoiceStates"
            :options="invoiceStates"
            @input="onInvoiceStateFilter"
            optionLabel="label"
            placeholder="Status"
            display="chip"
            :style="{ 'max-width': '150px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getInvoiceStateColor(slotProps.option.value)">
                {{ $t(slotProps.option.value) }}
              </b-badge>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Supplier -->
      <Column
        field="supplier"
        :header="$t('supplier')"
        sortable
        filterMatchMode="contains"
        filterField="supplierSearch"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.supplier">
            <span v-if="slotProps.data.supplier.name">
              {{ slotProps.data.supplier.name }}
            </span>
            <span v-else>
              {{ slotProps.data.supplier }}
            </span>
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Project -->
      <Column
        v-if="showProjectColumn"
        field="project"
        :header="$t('project')"
        sortable
        filterField="projectSearch"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.project">
            <router-link
              v-if="slotProps.data.project && slotProps.data.project.number"
              target="_blank"
              :to="{
                name: 'ProjectEditPage',
                params: { projectNumber: slotProps.data.project.number },
              }"
              >{{ slotProps.data.project.number.toString().padStart(4, '0') }}
              <i class="fa fa-sm fa-external-link"></i>
            </router-link>
            &nbsp;-&nbsp;
            {{ slotProps.data.project.customer.firstname }}
            {{ slotProps.data.project.customer.lastname }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('search')"
          />
        </template>
      </Column>

      <!-- Gross Amount -->
      <Column
        field="grossAmount"
        filterField="grossAmount"
        :header="$t('grossAmount')"
        sortable
        :headerStyle="{ width: '120px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'right' }"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.grossAmount">
            {{ formatCurrency(slotProps.data.grossAmount) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('grossAmount')"
          />
        </template>
      </Column>

      <!-- Net Amount -->
      <Column
        field="netAmount"
        :header="$t('netAmount')"
        sortable
        :headerStyle="{ width: '120px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'right' }"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.netAmount">
            {{ formatCurrency(slotProps.data.netAmount) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('netAmount')"
          />
        </template>
      </Column>

      <!-- Invoice Date -->
      <Column
        field="invoiceDate"
        :header="$t('invoiceDate')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
        filterField="invoiceDateRange"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.invoiceDate">
            {{ formatDate(slotProps.data.invoiceDate) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :style="{ 'max-width': '150px' }"
          />
        </template>
      </Column>

      <!-- Due Date -->
      <Column
        field="dueDate"
        :header="$t('dueDate')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
        filterField="dueDateRange"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.dueDate">
            {{ formatDate(slotProps.data.dueDate) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :style="{ 'max-width': '150px' }"
          />
        </template>
      </Column>

      <!-- Paid Date -->
      <Column
        field="paidDate"
        :header="$t('paidDate')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
        filterField="paidDateRange"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.paidDate">
            {{ formatDate(slotProps.data.paidDate) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :style="{ 'max-width': '150px' }"
          />
        </template>
      </Column>

      <!-- Created By -->
      <Column field="createdBy" :header="$t('createdBy')" sortable filterField="createdBy">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.createdBy">
            {{ slotProps.data.createdBy.firstname }} {{ slotProps.data.createdBy.lastname }}<br />
            <small>{{ slotProps.data.createdBy.email }}</small>
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Updated By -->
      <Column field="updatedBy" :header="$t('updatedBy')" sortable filterField="updatedBy">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.updatedBy">
            {{ slotProps.data.updatedBy.firstname }} {{ slotProps.data.updatedBy.lastname }}<br />
            <small>{{ slotProps.data.updatedBy.email }}</small>
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Actions -->
      <Column
        :header="$t('actions')"
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center; width: 100px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <button class="btn" @click="$emit('edit-invoice', slotProps.data)" title="Edit Invoice">
            <i class="link-icon fa fa-lg fa-edit"></i>
          </button>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import dayjs from 'dayjs';
import Calendar from 'primevue/calendar';
import { getInvoiceStateColor, getInvoiceTypeColor } from '@/helpers/colors';

export default {
  name: 'InvoicesTable',
  components: {
    DataTable,
    InputText,
    Column,
    MultiSelect,
    Calendar,
  },

  props: {
    projectFilter: {
      type: String,
      default: null,
    },
    tableStatePrefix: {
      type: String,
      default: 'invoices',
    },
    showProjectColumn: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      invoiceTableStateConfig: {
        name: this.tableStatePrefix,
        filters: {
          number: { value: null },
          invoiceNumber: { value: null },
          supplierSearch: { value: null },
          projectSearch: { value: null },
          grossAmount: { value: null },
          netAmount: { value: null },
          invoiceDateRange: { range: null },
          dueDateRange: { range: null },
          paidDateRange: { range: null },
          createdBy: { value: null },
          updatedBy: { value: null },
        },
        customFilters: {
          invoiceStates: [],
          invoiceTypes: [],
        },
        sorting: { sortField: 'number', sortOrder: -1 }, // Latest invoices first
      },
      defaultFilters: {
        number: { value: null },
        invoiceNumber: { value: null },
        supplierSearch: { value: null },
        projectSearch: { value: null },
        grossAmount: { value: null },
        netAmount: { value: null },
        invoiceDateRange: { range: null },
        dueDateRange: { range: null },
        paidDateRange: { range: null },
        createdBy: { value: null },
        updatedBy: { value: null },
      },
      defaultCustomFilters: {
        invoiceStates: [],
        invoiceTypes: [],
      },
    };
  },

  computed: {
    ...mapGetters(['getInvoices', 'getInvoiceCount']),
    ...mapGetters(['isLoading', 'getTableState', 'getEnumValues']),

    tableStateStore() {
      return this.getTableState(this.invoiceTableStateConfig.name);
    },

    invoiceStates() {
      const states = [];
      this.getEnumValues('InvoiceState').map((state) => {
        states.push({ value: state, label: this.$t(state) });
      });
      return states;
    },

    invoiceTypes() {
      const types = [];
      this.getEnumValues('InvoiceType').map((type) => {
        types.push({ value: type, label: this.$t(type) });
      });
      return types;
    },

    sumGrossAmount() {
      return (this.getInvoices || []).reduce(
        (total, invoice) => total + (invoice.grossAmount || 0),
        0
      );
    },

    sumNetAmount() {
      return (this.getInvoices || []).reduce(
        (total, invoice) => total + (invoice.netAmount || 0),
        0
      );
    },
  },

  methods: {
    ...mapActions(['initTableState', 'fetchEnumValues', 'fetchInvoicesPaginated']),

    getInvoiceStateColor,
    getInvoiceTypeColor,

    /**
     * Load remote table data
     */
    async loadTableData() {
      const filters = { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters };

      // Add project filter if specified
      if (this.projectFilter && this.projectFilter !== '_new') {
        filters.projectId = { value: this.projectFilter };
      }

      await this.fetchInvoicesPaginated({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: filters,
      });
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    /**
     * Filter by invoice states
     */
    async onInvoiceStateFilter(value) {
      const filteredStates = {
        invoiceStates: this.invoiceStates.filter((state) => value.includes(state)),
      };
      this.tableStateStore.setCustomFilters(filteredStates);
      await this.loadTableData();
    },

    /**
     * Filter by invoice types
     */
    async onInvoiceTypeFilter(value) {
      const filteredTypes = {
        invoiceTypes: this.invoiceTypes.filter((type) => value.includes(type)),
      };
      this.tableStateStore.setCustomFilters(filteredTypes);
      await this.loadTableData();
    },

    /**
     * Format date for display
     */
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('DD.MM.YYYY');
    },

    /**
     * Format currency for display
     */
    formatCurrency(amount, currency = 'EUR') {
      if (amount === null || amount === undefined) return '-';
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: currency,
      }).format(amount);
    },

    /**
     * Reset filter settings
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },
  },

  async created() {
    await this.initTableState(this.invoiceTableStateConfig);
  },

  async mounted() {
    await this.fetchEnumValues('InvoiceState');
    await this.fetchEnumValues('InvoiceType');
    await this.loadTableData();
  },
};
</script>

<style lang="scss">
.p-column-filter-clear-button {
  display: none;
  width: 0;
}

.p-column-filter-menu-button {
  display: none;
  width: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
}

.table-header-left {
  display: flex;
  justify-content: flex-start;
}

.table-header-sums {
  display: flex;
  align-items: start;
  column-gap: 1em;
}

.table-header-right {
  display: flex;
  justify-content: flex-end;
  column-gap: 1em;
}

/** Fix Dropdown overlay and table height */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}
</style>
